import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { TEMAS } from './temas-ayuda.jsx'

/**
 * Ayuda contextual de la consola.
 *
 * Un solo panel para toda la aplicación: el icono «?» de la barra superior lo abre, y ocupa el
 * alto completo desplazando TODO el layout (barra incluida) — es un flex, no un overlay: la
 * pantalla se estrecha y sigue siendo usable. Cada página registra su contenido con
 * `useAyuda(...)`; una página sin ayuda enseña el texto genérico, así el icono nunca engaña.
 *
 * Si estaba abierto se recuerda entre visitas (localStorage): quien trabaja con la ayuda al
 * lado no quiere reabrirla en cada página.
 */

const Ctx = createContext(null)

const abiertaGuardada = () => {
  try {
    // 'fn-ayuda' era la clave del panel viejo (solo del editor de funciones): se hereda.
    return localStorage.getItem('ayuda-abierta') === '1' || localStorage.getItem('fn-ayuda') === '1'
  } catch { return false }
}

export function AyudaProvider({ children }) {
  const [abierta, setAbierta] = useState(abiertaGuardada)
  const [contenido, setContenido] = useState(null) // { titulo, nodo } — lo contextual de la página
  const [tema, setTema] = useState(null)           // id de TEMAS — la ayuda navegable

  const guardarAbierta = (v) => {
    try { localStorage.setItem('ayuda-abierta', v ? '1' : '0') } catch { /* modo privado */ }
  }
  const alternar = () => setAbierta((v) => { guardarAbierta(!v); return !v })
  /** Abre el panel directamente en un tema del índice (lo usan la home y la paleta). */
  const abrirTema = (id) => { setTema(id); setAbierta(true); guardarAbierta(true) }

  return (
    <Ctx.Provider value={{ abierta, alternar, abrirTema, contenido, setContenido, tema, setTema }}>
      {children}
    </Ctx.Provider>
  )
}

export const usePanelAyuda = () => useContext(Ctx)

/**
 * Registra la ayuda de la página actual. `deps` controla cuándo se recalcula (por ejemplo, el
 * lenguaje de la función); al salir de la página el contenido se retira solo.
 */
export function useAyuda(fabricar, deps) {
  const { setContenido } = useContext(Ctx)
  useEffect(() => {
    setContenido(fabricar())
    return () => setContenido(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps las decide la página
  }, deps)
}

// Ancho a gusto de cada cual, entre lo legible y lo abusivo.
const ANCHO_MIN = 320
const ANCHO_MAX = 760
const ANCHO_DEFECTO = 400

const anchoGuardado = () => {
  try {
    const v = Number(localStorage.getItem('ayuda-ancho'))
    return v >= ANCHO_MIN && v <= ANCHO_MAX ? v : ANCHO_DEFECTO
  } catch { return ANCHO_DEFECTO }
}

export function PanelAyuda() {
  const { abierta, alternar, contenido, tema, setTema } = usePanelAyuda()
  const [ancho, setAncho] = useState(anchoGuardado)
  const arrastre = useRef(null)

  // El asa del borde izquierdo redimensiona con el puntero. setPointerCapture evita perder el
  // arrastre al pasar por encima de un iframe o al salir del asa; el ancho se guarda al soltar,
  // no en cada píxel.
  const alAgarrar = (e) => {
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    arrastre.current = { x: e.clientX, ancho }
  }
  const alMover = (e) => {
    if (!arrastre.current) return
    const nuevo = Math.min(ANCHO_MAX, Math.max(ANCHO_MIN, arrastre.current.ancho + (arrastre.current.x - e.clientX)))
    setAncho(nuevo)
  }
  const alSoltar = () => {
    if (!arrastre.current) return
    arrastre.current = null
    setAncho((v) => {
      try { localStorage.setItem('ayuda-ancho', String(v)) } catch { /* modo privado */ }
      return v
    })
  }

  if (!abierta) return null
  const temaActivo = TEMAS.find((t) => t.id === tema) ?? null
  return (
    <aside className="panel-ayuda" aria-label="Ayuda" style={{ width: ancho }}>
      <div className="ayuda-asa" role="separator" aria-orientation="vertical" title="Arrastra para cambiar el ancho"
           onPointerDown={alAgarrar} onPointerMove={alMover} onPointerUp={alSoltar} onPointerCancel={alSoltar} />
      <div className="ayuda-cabecera">
        <strong>{temaActivo?.titulo ?? contenido?.titulo ?? 'Ayuda'}</strong>
        <button className="aviso-cerrar" aria-label="Cerrar ayuda" onClick={alternar}>×</button>
      </div>

      {temaActivo ? (
        <>
          <button className="btn sm secondary" style={{ marginBottom: 4 }} onClick={() => setTema(null)}>
            ← Todos los temas
          </button>
          {temaActivo.nodo}
        </>
      ) : (
        <>
          {contenido?.nodo ?? (
            <p className="ayuda-nota">
              La ayuda de la plataforma, por temas. Cada página abre además su tema al entrar
              (el editor de funciones, por ejemplo, enseña la sintaxis de su lenguaje).
            </p>
          )}
          <nav className="ayuda-indice" aria-label="Temas de ayuda">
            <div className="ayuda-sec"><h3>Temas</h3></div>
            {TEMAS.map((t) => (
              <button key={t.id} className="ayuda-tema" onClick={() => setTema(t.id)}>
                {t.titulo} <span aria-hidden>→</span>
              </button>
            ))}
          </nav>
        </>
      )}
    </aside>
  )
}

export { AyudaSec, AyudaCod } from '../components/ayuda-partes.jsx'
